/**
 * @deprecated Use InvestorPortal layout via /portal/* routes.
 * This layout is being phased out. Routes served by this layout
 * should be migrated to /portal/* equivalents.
 * See MIGRATION.md for route mappings.
 *
 * Current /app/* routes still using this layout:
 *   /app/properties      → no direct /portal/* equivalent yet
 *   /app/properties/:id  → no direct /portal/* equivalent yet
 *   /app/portfolio       → no direct /portal/* equivalent yet
 *   /app/payment-history → no direct /portal/* equivalent yet
 */
import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { confirmSignOutWithUnsent } from '../../config/authRoutes';
import { motion, AnimatePresence } from 'framer-motion';
import { BRAND_ASSETS, TENANT } from '../../config';

// Fix: Created Layout component to resolve module not found error
// Robinhood-inspired minimalist navigation with disclaimer footer

const Layout: React.FC = () => {
  const { user, logout, connectWallet, wallet } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const isRedex = TENANT.id === 'redex';

  const handleLogout = async () => {
    if (!confirmSignOutWithUnsent(user?.id)) return; // unsent notes on this computer: ask first
    try {
      console.log('🚪 Logout button clicked!');
      // Don't close menu yet - let logout complete first
      console.log('🚪 Starting logout...');
      await logout();
      console.log('✅ Logout complete, navigating to /login');
      setShowUserMenu(false);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('❌ Logout error:', error);
      setShowUserMenu(false);
    }
  };

  const handleSettings = () => {
    console.log('⚙️ Settings button clicked!');
    // Navigate first, then close menu
    navigate('/profile');
    console.log('⚙️ Navigation triggered to /profile');
    setShowUserMenu(false);
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊', roles: ['user', 'investor', 'admin'] },
    { path: '/properties', label: TENANT.branding.propertyLabel, icon: '🏠', roles: ['user', 'investor', 'admin'] },
    { path: '/portfolio', label: 'Portfolio', icon: '💼', roles: ['investor', 'admin'] },
    ...(TENANT.features.crypto
      ? [{ path: '/blockchain', label: 'Blockchain', icon: '🔗', roles: ['user', 'investor', 'admin'] }]
      : []),
    { path: '/transactions', label: 'Transactions', icon: '💳', roles: ['user', 'investor', 'admin'] },
    { path: '/admin', label: 'Admin', icon: '⚙️', roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role || 'user')
  );

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Header Navigation - Robinhood Style */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center space-x-2">
              <img
                src={BRAND_ASSETS.logo.primary}
                alt={TENANT.branding.name}
                className="h-8 w-8"
              />
              <span className="text-xl font-semibold text-neutral-900">{TENANT.branding.name}</span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {filteredNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    location.pathname.startsWith(item.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
                  }`}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* User Actions */}
            <div className="flex items-center space-x-4">
              {/* Wallet Connection */}
              {!wallet?.connected && (
                <button
                  onClick={connectWallet}
                  className="btn-outline text-sm"
                >
                  Connect Wallet
                </button>
              )}

              {/* KYC Status */}
              {user?.kyc_status !== 'verified' && (
                <div className="badge badge-warning text-xs">
                  KYC Required
                </div>
              )}

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => {
                    console.log('👤 Avatar clicked, toggling menu. Current state:', showUserMenu);
                    setShowUserMenu(!showUserMenu);
                  }}
                  className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                >
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-neutral-900">{user?.email}</p>
                    <p className="text-xs text-neutral-500 capitalize">{user?.role}</p>
                  </div>
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                </button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {showUserMenu && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={(e) => {
                          console.log('🔴 Backdrop clicked - closing menu');
                          e.stopPropagation();
                          setShowUserMenu(false);
                        }}
                      />
                      
                      {/* Menu */}
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => {
                          console.log('🎯 Menu container clicked - stopping propagation');
                          e.stopPropagation();
                        }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-neutral-200 overflow-hidden z-50"
                      >
                        <div className="p-3 border-b border-neutral-200">
                          <p className="text-sm font-medium text-neutral-900 truncate">
                            {user?.email}
                          </p>
                          <p className="text-xs text-neutral-500 mt-1 capitalize">
                            {user?.role} Account
                          </p>
                        </div>

                        <div className="p-2">
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              console.log('⚙️ Settings MOUSEDOWN captured');
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              console.log('⚙️ Settings button CLICK detected');
                              e.stopPropagation();
                              e.preventDefault();
                              handleSettings();
                            }}
                            onMouseEnter={() => console.log('⚙️ Settings button HOVER')}
                            className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-neutral-700 hover:bg-neutral-100 transition-colors"
                          >
                            <span>⚙️</span>
                            <span className="text-sm font-medium">Settings</span>
                          </button>

                          <button
                            type="button"
                            onMouseDown={(e) => {
                              console.log('🚪 Logout MOUSEDOWN captured');
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              console.log('🚪 Logout button CLICK detected');
                              e.stopPropagation();
                              e.preventDefault();
                              handleLogout();
                            }}
                            onMouseEnter={() => console.log('🚪 Logout button HOVER')}
                            className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <span>🚪</span>
                            <span className="text-sm font-medium">Logout</span>
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden border-t border-neutral-200">
          <div className="grid grid-cols-5 gap-1 py-2 px-2">
            {filteredNavItems.slice(0, 3).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center space-y-1 px-2 py-2 rounded-lg text-xs font-medium transition-colors duration-200 ${
                  location.pathname.startsWith(item.path)
                    ? 'text-primary bg-primary/10'
                    : 'text-neutral-600'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            ))}
            
            {/* Settings Button */}
            <button
              onClick={handleSettings}
              className="flex flex-col items-center space-y-1 px-2 py-2 rounded-lg text-xs font-medium text-neutral-600 transition-colors duration-200"
            >
              <span className="text-lg">⚙️</span>
              <span>Settings</span>
            </button>
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex flex-col items-center space-y-1 px-2 py-2 rounded-lg text-xs font-medium text-red-600 transition-colors duration-200"
            >
              <span className="text-lg">🚪</span>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Disclaimer Footer */}
      <footer className="bg-white border-t border-neutral-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
            <div className="text-sm text-neutral-600">
              <p>
                © 2026 {TENANT.branding.name}.{' '}
                {isRedex ? 'Platform usage involves risk.' : 'Real estate investments involve substantial risk.'}
              </p>
              <button
                onClick={() => setShowDisclaimer(true)}
                className="text-primary hover:text-primary/90 underline"
              >
                View Risk Disclaimers
              </button>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-neutral-500">
              {isRedex ? (
                <>
                  <span>Audit-Ready</span>
                  <span>•</span>
                  <span>Non-Custodial</span>
                  <span>•</span>
                  <span>Blockchain Verified</span>
                </>
              ) : (
                <>
                  <span>SEC Regulated</span>
                  <span>•</span>
                  <span>Non-Custodial</span>
                  <span>•</span>
                  <span>Blockchain Verified</span>
                </>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* Risk Disclaimer Modal */}
      <AnimatePresence>
        {showDisclaimer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowDisclaimer(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-neutral-900">
                  {isRedex ? 'Platform Risk Disclaimer' : 'Investment Risk Disclaimer'}
                </h2>
                <button
                  onClick={() => setShowDisclaimer(false)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4 text-sm text-neutral-700">
                <div>
                  <h3 className="font-medium text-neutral-900 mb-2">⚠️ Investment Risks</h3>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Property values may decline, resulting in loss of principal</li>
                    <li>Dividends are not guaranteed and may fluctuate or cease entirely</li>
                    <li>Liquidity is limited - you may not be able to sell shares when desired</li>
                    <li>Past performance does not guarantee future results</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-medium text-neutral-900 mb-2">🔗 Blockchain Technology Risks</h3>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Smart contract vulnerabilities may lead to loss of funds</li>
                    <li>Network congestion may delay transactions or increase fees</li>
                    <li>Blockchain transactions are immutable and cannot be reversed</li>
                    <li>You are responsible for maintaining wallet security</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-medium text-neutral-900 mb-2">📋 Compliance</h3>
                  {isRedex ? (
                    <ul className="list-disc list-inside space-y-1">
                      <li>KYC/AML verification may be required for certain flows</li>
                      <li>Operational controls and approvals may apply</li>
                      <li>Tax and reporting implications may apply - consult your advisor</li>
                    </ul>
                  ) : (
                    <ul className="list-disc list-inside space-y-1">
                      <li>Securities offered under Regulation D/A+ exemptions</li>
                      <li>KYC verification required for investments over $1,000</li>
                      <li>Investment limits apply based on investor accreditation</li>
                      <li>Tax implications may apply - consult your tax advisor</li>
                    </ul>
                  )}
                </div>
                
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <p className="text-foreground font-medium">
                    {isRedex
                      ? 'Use only funds and credentials you can afford to expose to operational and market risks. Verify every destination and approval step.'
                      : 'Only invest money you can afford to lose completely. This platform is for sophisticated investors who understand the risks.'}
                  </p>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDisclaimer(false)}
                  className="btn-primary"
                >
                  {isRedex ? 'I Understand' : 'I Understand the Risks'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;
